import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Trash2, Upload, Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getToken } from '@/utils/tokenManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const ContinuityAssetDocuments = ({ assetId, readOnly = false }) => {
  const [docs, setDocs] = useState([]);
  const [allowedTags, setAllowedTags] = useState(['Title', 'Insurance certificate', 'Other']);
  const [max, setMax] = useState(6);
  const [tag, setTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/continuity-assets/${assetId}/documents`, { headers: authHeaders() });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Could not load documents');
      setDocs(data.documents || []);
      setMax(data.max || 6);
      if (data.allowed_tags) setAllowedTags(data.allowed_tags);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [assetId]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (file) => {
    if (!tag) { toast.error('Pick a document type first.'); return; }
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_tag', tag);
      const resp = await fetch(`${BACKEND_URL}/api/continuity-assets/${assetId}/documents`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Upload failed');
      toast.success('Document uploaded.');
      await refresh();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const download = async (doc) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/continuity-assets/${assetId}/documents/${doc.doc_id}/download`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const doDelete = async (doc) => {
    if (readOnly) return;
    if (!window.confirm(`Remove "${doc.filename}"?`)) return;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/continuity-assets/${assetId}/documents/${doc.doc_id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!resp.ok) throw new Error('Delete failed');
      await refresh();
      toast.success('Document removed.');
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-3" data-testid="ca-docs-panel">
      <div className="flex items-center gap-2">
        <Paperclip className="w-4 h-4 text-slate-600" />
        <h4 className="text-sm font-semibold text-slate-800">Attached documents</h4>
        <span className="text-xs text-slate-500">{docs.length} of {max}</span>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-200 p-3">
          <div className="grow min-w-[180px]">
            <Label className="text-xs">Document type</Label>
            <Select value={tag} onValueChange={setTag}>
              <SelectTrigger data-testid="ca-doc-tag-select"><SelectValue placeholder="Pick a type" /></SelectTrigger>
              <SelectContent>
                {allowedTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <input
              ref={fileRef} type="file" data-testid="ca-doc-file-input"
              onChange={(e) => submit(e.target.files?.[0])}
              accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx,.xls,.xlsx,.csv"
              className="hidden"
            />
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || docs.length >= max}
              data-testid="ca-doc-upload-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      )}

      {loading && <p className="text-xs text-slate-500">Loading documents…</p>}
      {!loading && docs.length === 0 && <p className="text-xs text-slate-500">No documents attached yet.</p>}
      {!loading && docs.length > 0 && (
        <ul className="space-y-1">
          {docs.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm text-slate-800 truncate">{d.filename}</p>
                <p className="text-xs text-slate-500">
                  {d.doc_tag ? <span className="text-emerald-700">{d.doc_tag}</span> : null}
                  {d.doc_tag ? ' · ' : ''}{Math.round((d.size_bytes || 0) / 1024)} KB
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => download(d)} data-testid={`ca-doc-download-${d.doc_id}`}>
                  <Download className="w-4 h-4 text-slate-600" />
                </Button>
                {!readOnly && (
                  <Button variant="ghost" size="icon" onClick={() => doDelete(d)} data-testid={`ca-doc-delete-${d.doc_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ContinuityAssetDocuments;
